// Data the overview can show. Each source is one thing a block can be bound
// to; the layout (which blocks, in what order, how wide) is saved JSON owned
// by the kit's Dashboard, so nothing here decides placement.
//
// UI-WORDING RULE: labels use plain language (Money owed to you, never Aged
// receivables). Technical terms live in `description` only.

import { formatMoney } from "@sylviq/ui-core";
import type {
  AnyDataSource,
  DashboardState,
  Preset,
} from "@sylviq/ui-react-web/components/dashboard/model";
import { Link } from "@tanstack/react-router";
import {
  getAgedPayables,
  getAgedReceivables,
  getCashFlow,
  getCashFlowForecast,
  getProfitAndLoss,
  getReadCacheGeneration,
  listBankAccounts,
  listBankTransactions,
  getConversionBalances,
  listChartAccounts,
  listInvoices,
  listJournalLines,
} from "@/lib/api";
import { pnlNetIncome, pnlSectionSubtotal } from "@/features/reports/pnl-summary";

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

/** First and last day of the month `offset` months before this one. */
function monthRange(
  offset: number,
  anchor: Date = new Date(),
): {
  start: string;
  end: string;
  label: string;
} {
  const first = new Date(
    Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() - offset, 1),
  );
  const last = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0),
  );
  return {
    start: isoDay(first),
    end: isoDay(last),
    label: first.toLocaleString("en-US", { month: "short", timeZone: "UTC" }),
  };
}

function daysOverdue(days: number): string {
  return `${days} ${days === 1 ? "day" : "days"} overdue`;
}

/** Ungrouped, overdue lines from an aged report's buckets. */
function overdueFromBuckets<TRow extends { days_overdue: number; amount_due_cents: number }>(
  buckets: Array<{ invoices?: TRow[]; bills?: TRow[] }>,
  key: "invoices" | "bills",
): { count: number; totalCents: number } {
  let count = 0;
  let totalCents = 0;
  for (const bucket of buckets) {
    for (const row of bucket[key] ?? []) {
      if (row.days_overdue > 0) {
        count += 1;
        totalCents += row.amount_due_cents;
      }
    }
  }
  return { count, totalCents };
}

const savedLayoutOnly = new Set([
  "recent-activity",
  "overdue-invoices",
  "overdue-bills",
]);

// Overview reads are cached in memory only. Nothing is written to
// localStorage, sessionStorage, cookies, or IndexedDB: a balance, name, or
// document list must not outlive the signed-in page session. A successful
// mutation bumps the API generation and makes the next read fresh.
const OVERVIEW_CACHE_TTL_MS = 5 * 60_000;
interface OverviewCacheEntry {
  generation: number;
  at: number;
  promise: Promise<unknown>;
}
const overviewReads = new Map<string, OverviewCacheEntry>();

function cachedOverviewRead<T>(key: string, load: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const generation = getReadCacheGeneration();
  const hit = overviewReads.get(key);
  if (
    hit &&
    hit.generation === generation &&
    now - hit.at < OVERVIEW_CACHE_TTL_MS
  ) {
    return hit.promise as Promise<T>;
  }
  let promise: Promise<T>;
  promise = load().catch((error: unknown) => {
    if (overviewReads.get(key)?.promise === promise) overviewReads.delete(key);
    throw error;
  });
  overviewReads.set(key, { generation, at: now, promise });
  return promise;
}

/** Test/manual seam: drop every cached Overview read. */
export function clearOverviewReadCache(): void {
  overviewReads.clear();
}

export function createSources(
  organizationKey: string,
  legalEntityKey: string,
): AnyDataSource[] {
  const prefix = `${organizationKey}:${legalEntityKey}`;
  const read = <T,>(key: string, load: () => Promise<T>) =>
    cachedOverviewRead(`${prefix}:${key}`, load);
  const agedReceivables = () => read("aged-receivables", getAgedReceivables);
  const agedPayables = () => read("aged-payables", getAgedPayables);
  const profitAndLoss = (start: string, end: string) =>
    read(`profit-and-loss:${start}:${end}`, () =>
      getProfitAndLoss({ start_date: start, end_date: end }),
    );
  const bankAccounts = () => read("bank-accounts", listBankAccounts);
  const bankLines = (accountId: string) =>
    read(`bank-lines:${accountId}`, () => listBankTransactions(accountId));
  const journalLines = () => read("journal-lines", listJournalLines);

  const sources: AnyDataSource[] = [
    {
      id: "owed-to-you",
      label: "Money owed to you",
      description: "Open customer invoices (accounts receivable)",
      kind: "metric",
      async load() {
        const report = await agedReceivables();
        return {
          value: money(report.grand_total_cents),
          detail: "Open customer invoices",
        };
      },
    },
    {
      id: "you-owe",
      label: "Money you owe",
      description: "Unpaid supplier bills (accounts payable)",
      kind: "metric",
      async load() {
        const report = await agedPayables();
        return {
          value: money(report.grand_total_cents),
          detail: "Unpaid supplier bills",
        };
      },
    },
    {
      id: "cash-on-hand",
      label: "Cash on hand",
      description: "Closing balance from the cash-flow report",
      kind: "metric",
      async load() {
        const report = await getCashFlow();
        return {
          value: money(report.closing_cash_balance_cents),
          detail: "Closing balance from the cash-flow report",
        };
      },
    },
    {
      id: "profit-this-month",
      label: "Profit this month",
      description: "Revenue less expenses, month to date",
      kind: "metric",
      async load() {
        const { start, end } = monthRange(0);
        const report = await profitAndLoss(start, end);
        const cents = report.grand_total_cents ?? 0;
        return {
          value: money(cents),
          detail: cents >= 0 ? "Ahead this month" : "Behind this month",
          trend: cents >= 0 ? "up" : "down",
          favorable: cents >= 0,
        };
      },
    },
    {
      id: "revenue-this-month",
      label: "Revenue this month",
      description:
        "Income and other income this month, from the profit and loss",
      kind: "metric",
      async load() {
        const { start, end, label } = monthRange(0);
        const report = await profitAndLoss(start, end);
        const cents = pnlSectionSubtotal(report, "revenue");
        if (cents === null) {
          return {
            value: "—",
            detail: `No revenue reported for ${label}.`,
          };
        }
        return { value: money(cents), detail: `${label} to date` };
      },
    },
    {
      id: "expenses-this-month",
      label: "Expenses this month",
      description:
        "Cost of goods sold, expenses, and other expenses this month",
      kind: "metric",
      async load() {
        const { start, end, label } = monthRange(0);
        const report = await profitAndLoss(start, end);
        const cents = pnlSectionSubtotal(report, "expense");
        if (cents === null) {
          return {
            value: "—",
            detail: `No expenses reported for ${label}.`,
          };
        }
        return { value: money(cents), detail: `${label} to date` };
      },
    },
    {
      id: "net-income-this-month",
      label: "Net income this month",
      description:
        "Revenue less expenses for the month, from the profit & loss section subtotals",
      kind: "metric",
      async load() {
        const { start, end, label } = monthRange(0);
        const report = await profitAndLoss(start, end);
        const cents = pnlNetIncome(report);
        if (cents === null) {
          return {
            value: "—",
            detail: `No profit & loss reported for ${label}.`,
          };
        }
        return {
          value: money(cents),
          detail:
            cents >= 0
              ? `Ahead ${label} to date`
              : `Behind ${label} to date`,
          trend: cents >= 0 ? "up" : "down",
          favorable: cents >= 0,
        };
      },
    },
    {
      id: "cash-forecast",
      label: "180-day cash flow forecast",
      description:
        "Expected money in and out over the next 180 days, from open invoices and bills",
      kind: "series",
      async load() {
        const forecast = await getCashFlowForecast({ weeks: 26 });
        return {
          x: "week",
          points: forecast.weeks.map((week) => ({
            week: new Date(`${week.week_of.slice(0, 10)}T00:00:00Z`).toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric", timeZone: "UTC" },
            ),
            moneyIn: week.inflows_cents / 100,
            moneyOut: week.outflows_cents / 100,
            net: week.net_cents / 100,
          })),
          series: [
            { key: "moneyIn", label: "Money in" },
            { key: "moneyOut", label: "Money out" },
            { key: "net", label: "Net" },
          ],
          formatValue: (value) => money(Math.round(value * 100)),
        };
      },
    },
    {
      id: "profit-by-month",
      label: "Profit by month",
      description: "Revenue less expenses, trailing six months of posted activity",
      kind: "series",
      async load() {
        // Anchor the trend to the books, not the wall clock: an organization whose
        // latest postings are older than this month still gets a populated
        // chart instead of six empty months.
        const lines = await journalLines();
        const latestDate = lines.length > 0 ? lines[lines.length - 1]!.entry_date : null;
        const anchor = latestDate ? new Date(`${latestDate}T00:00:00Z`) : new Date();
        const months = [5, 4, 3, 2, 1, 0].map((offset) => monthRange(offset, anchor));
        const reports = await Promise.all(
          months.map((m) => profitAndLoss(m.start, m.end)),
        );
        return {
          x: "month",
          points: months.map((m, i) => ({
            month: m.label,
            profit: (reports[i]?.grand_total_cents ?? 0) / 100,
          })),
          series: [{ key: "profit", label: "Profit" }],
          formatValue: (value) => money(Math.round(value * 100)),
        };
      },
    },
    {
      id: "owed-by-age",
      label: "Who owes you, by age",
      description: "Open invoices bucketed by days overdue",
      kind: "series",
      async load() {
        const report = await agedReceivables();
        return {
          x: "bucket",
          points: report.buckets.map((b) => ({
            bucket: b.label,
            owed: b.total_cents / 100,
          })),
          series: [{ key: "owed", label: "Owed" }],
          formatValue: (value) => money(Math.round(value * 100)),
        };
      },
    },
    {
      id: "payables-by-age",
      label: "What you owe, by age",
      description: "Open supplier bills bucketed by days overdue",
      kind: "series",
      async load() {
        const report = await agedPayables();
        return {
          x: "bucket",
          points: report.buckets.map((b) => ({
            bucket: b.label,
            owed: b.total_cents / 100,
          })),
          series: [{ key: "owed", label: "Owed" }],
          formatValue: (value) => money(Math.round(value * 100)),
        };
      },
    },
    {
      id: "overdue-invoices",
      label: "Overdue invoices",
      description: "The invoices furthest past due",
      kind: "table",
      async load() {
        const report = await agedReceivables();
        const rows = report.buckets
          .flatMap((bucket) => bucket.invoices ?? [])
          .filter((row) => row.days_overdue > 0)
          .sort((a, b) => b.days_overdue - a.days_overdue);
        return {
          columns: [
            { key: "invoice", label: "Invoice" },
            { key: "late", label: "Late" },
            { key: "due", label: "Owed", align: "end" as const },
          ],
          rows: rows.map((row) => ({
            id: row.id,
            cells: {
              invoice: (
                <Link
                  to="/invoices/$invoiceId"
                  params={{ invoiceId: row.id }}
                  className="font-medium hover:underline"
                >
                  {row.invoice_number} · {row.customer_name}
                </Link>
              ),
              late: daysOverdue(row.days_overdue),
              due: money(row.amount_due_cents),
            },
          })),
          empty: "Nothing overdue — every open invoice is still within terms.",
          footer: { label: "All invoices", href: "/invoices" },
        };
      },
    },
    {
      id: "overdue-bills",
      label: "Overdue bills",
      description: "The bills furthest past due",
      kind: "table",
      async load() {
        const report = await agedPayables();
        const rows = report.buckets
          .flatMap((bucket) => bucket.bills ?? [])
          .filter((row) => row.days_overdue > 0)
          .sort((a, b) => b.days_overdue - a.days_overdue);
        return {
          columns: [
            { key: "bill", label: "Bill" },
            { key: "late", label: "Late" },
            { key: "due", label: "Owed", align: "end" as const },
          ],
          rows: rows.map((row) => ({
            id: row.id,
            cells: {
              bill: (
                <Link
                  to="/bills/$billId"
                  params={{ billId: row.id }}
                  className="font-medium hover:underline"
                >
                  {row.bill_number} · {row.supplier_name}
                </Link>
              ),
              late: daysOverdue(row.days_overdue),
              due: money(row.amount_due_cents),
            },
          })),
          empty: "Nothing overdue — every open bill is still within terms.",
          footer: { label: "All bills", href: "/bills" },
        };
      },
    },
    {
      id: "uncategorized-bank-lines",
      label: "Bank lines to categorize",
      description:
        "Imported bank transactions not yet reconciled to the ledger",
      kind: "metric",
      async load() {
        const accounts = await bankAccounts();
        if (accounts.length === 0) {
          return { value: "—", detail: "No bank accounts set up yet." };
        }
        const perAccount = await Promise.all(
          accounts.map((account) => bankLines(account.id)),
        );
        const count = perAccount
          .flat()
          .filter((line) => line.status === "unreconciled").length;
        if (count === 0) {
          return {
            value: "0",
            detail: "Every imported bank line is reconciled",
            trend: "flat",
            favorable: true,
          };
        }
        return {
          value: String(count),
          detail:
            count === 1 ? "1 line needs a category" : `${count} lines need a category`,
          trend: "down",
          favorable: false,
        };
      },
    },
    {
      id: "attention",
      label: "Needs attention",
      description: "Overdue invoices and bills, and uncategorized bank lines",
      kind: "table",
      async load() {
        const [receivables, payables, accounts] = await Promise.all([
          agedReceivables(),
          agedPayables(),
          bankAccounts().catch((): never[] => []),
        ]);
        const invoice = overdueFromBuckets(receivables.buckets, "invoices");
        const bill = overdueFromBuckets(payables.buckets, "bills");
        let bankCount: number | null = null;
        if (accounts.length > 0) {
          const perAccount = await Promise.all(
            accounts.map((account) => bankLines(account.id)),
          );
          bankCount = perAccount
            .flat()
            .filter((line) => line.status === "unreconciled").length;
        }
        const rows: Array<{
          id: string;
          to: string;
          search?: Record<string, string>;
          label: string;
          value: string;
          tone?: string;
        }> = [
          {
            id: "overdue-invoices",
            to: "/invoices",
            search: { status: "overdue" },
            label: "Overdue invoices",
            value:
              invoice.count === 0
                ? "None"
                : `${invoice.count} · ${money(invoice.totalCents)}`,
            tone: invoice.count > 0 ? "text-[var(--danger)]" : undefined,
          },
          {
            id: "overdue-bills",
            to: "/bills",
            search: { status: "overdue" },
            label: "Overdue bills",
            value:
              bill.count === 0 ? "None" : `${bill.count} · ${money(bill.totalCents)}`,
            tone: bill.count > 0 ? "text-[var(--danger)]" : undefined,
          },
          {
            id: "bank",
            to: "/banking",
            label: "Bank lines to categorize",
            value:
              bankCount === null ? "—" : bankCount === 0 ? "None" : String(bankCount),
            tone: bankCount && bankCount > 0 ? "text-[var(--danger)]" : undefined,
          },
        ];
        return {
          columns: [
            { key: "item", label: "What" },
            { key: "value", label: "How much", align: "end" as const },
          ],
          rows: rows.map((row) => ({
            id: row.id,
            cells: {
              item: (
                <Link
                  to={row.to}
                  search={row.search}
                  className="font-medium hover:underline"
                >
                  {row.label}
                </Link>
              ),
              value: <span className={row.tone}>{row.value}</span>,
            },
          })),
          empty: "Nothing needs attention right now.",
        };
      },
    },
    {
      id: "recent-activity",
      label: "Recent activity",
      description: "The latest posted general-ledger lines",
      kind: "table",
      async load() {
        const lines = await journalLines();
        const latest = [...lines]
          .sort((a, b) => b.entry_date.localeCompare(a.entry_date))
          .slice(0, 25);
        return {
          columns: [
            { key: "date", label: "Date" },
            { key: "memo", label: "Description" },
            { key: "account", label: "Account" },
            { key: "amount", label: "Amount", align: "end" as const },
          ],
          rows: latest.map((line) => ({
            id: line.line_id,
            cells: {
              date: line.entry_date.slice(0, 10),
              memo: line.memo || "—",
              account: line.account_code,
              amount: money(line.debit_minor - line.credit_minor),
            },
          })),
          empty: "No entries posted yet.",
          footer: { label: "General ledger", href: "/journal" },
        };
      },
    },
    {
      id: "setup-steps",
      label: "Getting set up",
      description: "The first steps for a new set of books",
      kind: "checklist",
      async load() {
        const [
          invoices,
          accounts,
          lines,
          conversion,
          bankAccountRows,
        ] = await Promise.all([
            listInvoices().catch((): never[] => []),
            listChartAccounts().catch((): never[] => []),
            journalLines().catch((): never[] => []),
            getConversionBalances().catch(() => null),
            bankAccounts().catch((): never[] => []),
          ]);
        return {
          banner:
            !accounts.length && !lines.length && !invoices.length
              ? {
                  title: "Start your books with the basics",
                  detail:
                    "Set up accounts, opening balances, and your first invoice to turn this checklist into a working overview.",
                }
              : undefined,
          dismissKey: `niche-accounting.setup-checklist:${organizationKey}`,
          steps: [
            {
              id: "chart",
              label: "Set up your chart of accounts",
              detail:
                "Start from the template list of accounts, then rename or add your own.",
              done: accounts.length > 0,
              href: "/chart-of-accounts",
            },
            {
              id: "opening",
              label: "Enter opening balances",
              detail:
                "Record one journal dated the day before these books start. It is in the books once it is posted.",
              done: conversion?.status === "posted",
              href: "/opening-balances",
            },
            {
              id: "invoice",
              label: "Send your first invoice",
              detail:
                "Bill a customer so money owed to you starts showing up here.",
              done: invoices.length > 0,
              href: "/invoices",
            },
            {
              id: "bank",
              label: "Add your bank accounts",
              detail:
                "Open each account on the Banking page, then import its statements and match the lines to your books.",
              done: bankAccountRows.length > 0,
              href: "/banking",
            },
          ],
        };
      },
    },
  ];
  for (const source of sources) {
    if (savedLayoutOnly.has(source.id)) Object.assign(source, { offered: false });
  }
  return sources;
}

export const defaultPresets: Preset[] = [
  {
    id: "money-picture",
    name: "Money picture",
    // A first-run financial overview: the P&L headline numbers (from section
    // subtotals, not just the net), the profit trend, both aging charts,
    // and an action list. Overdue tables stay on Collections. Recent
    // activity stays loadable for a layout that already saved it.
    widgets: [
      { id: "setup", type: "checklist", source: "setup-steps", span: 12 },
      { id: "revenue", type: "metric", source: "revenue-this-month", span: 3 },
      { id: "expenses", type: "metric", source: "expenses-this-month", span: 3 },
      { id: "net", type: "metric", source: "net-income-this-month", span: 3 },
      { id: "cash", type: "metric", source: "cash-on-hand", span: 3 },
      { id: "profit", type: "chart", source: "profit-by-month", span: 12 },
      { id: "forecast", type: "chart", source: "cash-forecast", span: 12 },
      {
        id: "owed-age",
        type: "chart",
        source: "owed-by-age",
        span: 6,
        options: { style: "bar" },
      },
      {
        id: "owe-age",
        type: "chart",
        source: "payables-by-age",
        span: 6,
        options: { style: "bar" },
      },
      { id: "attention", type: "table", source: "attention", span: 12 },
    ],
  },
  {
    id: "collections",
    name: "Collections",
    widgets: [
      { id: "owed", type: "metric", source: "owed-to-you", span: 4 },
      {
        id: "age",
        type: "chart",
        source: "owed-by-age",
        span: 8,
        options: { style: "bar" },
      },
      {
        id: "od-inv",
        type: "table",
        source: "overdue-invoices",
        span: 12,
        options: { limit: 10 },
      },
    ],
  },
  {
    id: "cash-and-profit",
    name: "Cash and profit",
    widgets: [
      { id: "cash", type: "metric", source: "cash-on-hand", span: 6 },
      {
        id: "profit-now",
        type: "metric",
        source: "net-income-this-month",
        span: 6,
      },
      {
        id: "profit",
        type: "chart",
        source: "profit-by-month",
        span: 12,
        options: { style: "bar" },
      },
      { id: "forecast", type: "chart", source: "cash-forecast", span: 12 },
      {
        id: "activity",
        type: "table",
        source: "recent-activity",
        span: 12,
        options: { limit: 8 },
      },
    ],
  },
];


const CASH_FORECAST_SOURCE = "cash-forecast";
const MONEY_PICTURE_PRESET = "money-picture";

/**
 * Older saved Overview layouts predate the cash-forecast widget. Add the
 * built-in widget back to the Money picture layout without disturbing any
 * other saved block, size, or order. The result is display-only until the
 * user saves a customization; this function never writes storage.
 */
export function ensureCashForecastInLayout(
  state: DashboardState,
): DashboardState {
  const preset = state.presets.find((item) => item.id === MONEY_PICTURE_PRESET);
  if (
    !preset ||
    preset.widgets.some((widget) => widget.source === CASH_FORECAST_SOURCE)
  ) {
    return state;
  }
  const template = defaultPresets
    .find((item) => item.id === MONEY_PICTURE_PRESET)
    ?.widgets.find((widget) => widget.source === CASH_FORECAST_SOURCE);
  if (!template) return state;
  const widgets = [...preset.widgets];
  const attention = widgets.findIndex((widget) => widget.source === "attention");
  const id = widgets.some((widget) => widget.id === template.id)
    ? `${template.id}-forecast`
    : template.id;
  widgets.splice(attention >= 0 ? attention : widgets.length, 0, {
    ...template,
    id,
  });
  return {
    ...state,
    presets: state.presets.map((item) =>
      item.id === preset.id ? { ...item, widgets } : item,
    ),
  };
}
